import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CheckCircle2,
  ChevronLeft,
  Circle,
  Clock3,
  GraduationCap,
  UserRound,
  UsersRound,
} from "lucide-react";
import { ghostButtonClass, Status } from "../components/ui";
import { pdDayDetailPath } from "../lib/pdDayRoutes";
import { usePDDayNumber } from "../pddays/usePDDays";
import { useLiveProgress } from "../progress/useLiveProgress";
import type { RosterEntry } from "../types";

function initials(name: string, fallback: string): string {
  const parts = (name || fallback).trim().split(/\s+/).filter(Boolean);
  return (
    parts
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "T"
  );
}

function MetricCard({
  label,
  value,
  total,
  percent,
  status,
  tone,
}: {
  label: string;
  value: number;
  total: number;
  percent: number;
  status?: string;
  tone: "success" | "accent";
}) {
  const success = tone === "success";
  return (
    <section className="min-w-0 rounded-xl bg-surface p-3 shadow-raised">
      <p className="truncate text-[10px] font-bold uppercase tracking-wide text-app-muted">
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold leading-none text-app-ink">
        <span className={success ? "text-success-text" : "text-accent"}>{value}</span>
        <span className="ml-1 text-sm text-app-muted">/{total}</span>
      </p>
      {status && (
        <div
          className={`mt-2 flex items-center gap-1 text-[10px] font-semibold ${
            success ? "text-success-text" : "text-accent"
          }`}
        >
          {success ? <CheckCircle2 size={12} /> : <Circle size={9} fill="currentColor" />}
          <span className="truncate">{status}</span>
        </div>
      )}
      <div className="mt-2 h-1 overflow-hidden rounded-full bg-app-field">
        <div
          className={`h-full rounded-full ${success ? "bg-success" : "bg-accent"}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </section>
  );
}

function TeacherRow({
  teacher,
  completed,
  completedLabel,
  inProgressLabel,
  loggedInLabel,
  offlineLabel,
}: {
  teacher: RosterEntry;
  completed: boolean;
  completedLabel: string;
  inProgressLabel: string;
  loggedInLabel: string;
  offlineLabel: string;
}) {
  const name = teacher.name || teacher.assigned_id;
  return (
    <li className="grid grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-2 rounded-lg bg-app-field px-2 py-2">
      <span className="grid h-8 w-8 place-items-center rounded-full bg-accent-soft text-[10px] font-bold text-accent">
        {initials(name, teacher.assigned_id)}
      </span>
      <div className="min-w-0">
        <p className="truncate text-xs font-semibold text-app-ink">{name}</p>
        <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[9px] text-app-muted">
          <span
            className={`flex shrink-0 items-center gap-1 ${teacher.online ? "text-accent" : "text-app-muted"}`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${teacher.online ? "bg-accent" : "bg-app-icon"}`}
            />
            {teacher.online ? loggedInLabel : offlineLabel}
          </span>
        </div>
      </div>
      <span
        className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-1 text-[9px] font-semibold ${
          completed ? "bg-accent-soft text-accent" : "bg-surface text-app-muted"
        }`}
      >
        {completed ? <CheckCircle2 size={10} /> : <Clock3 size={10} />}
        {completed ? completedLabel : inProgressLabel}
      </span>
    </li>
  );
}

export function PDDayProgress() {
  const { t } = useTranslation();
  const { sessionID = "" } = useParams();
  const navigate = useNavigate();
  const { data, loading, error } = useLiveProgress(sessionID);
  const dayNumber = usePDDayNumber(sessionID);

  const back = (
    <button
      type="button"
      className={`${ghostButtonClass} mb-4`}
      onClick={() => navigate(pdDayDetailPath(sessionID))}
    >
      <ChevronLeft size={16} />
      {dayNumber ? t("pdDayOrdinal", { number: dayNumber }) : t("backToPDDay")}
    </button>
  );

  if (loading)
    return <Status variant="loading" title={t("loading")} message={t("loadingProgress")} />;
  if (error || !data) {
    return (
      <>
        {back}
        <Status
          variant="error"
          title={t("progressUnavailable")}
          message={error || t("noProgress")}
        />
      </>
    );
  }

  const { detail, stats, progress, roster } = data;
  const itemCount = progress.items.length;
  const completedTeachers = roster.filter(
    (teacher) =>
      teacher.module_submitted || (itemCount > 0 && teacher.activities_completed >= itemCount)
  ).length;
  const total = roster.length || progress.roster_size;
  const completionPercent = total ? Math.round((completedTeachers / total) * 100) : 0;
  const onlinePercent = total ? Math.round((stats.online / total) * 100) : 0;

  return (
    <div className="flex flex-col gap-4 pb-6">
      {back}
      <header className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-bold tracking-tight text-app-ink">
            {detail.status === "ended"
              ? dayNumber
                ? t("pdDaySummaryOrdinal", { number: dayNumber })
                : t("pdDaySummaryTitle")
              : dayNumber
                ? t("pdDayLiveProgressOrdinal", { number: dayNumber })
                : t("pdDayLiveProgress")}
          </h1>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {(detail.facilitators.length > 0
          ? detail.facilitators
          : [{ name: detail.facilitator_name, started_at: null, ended_at: null }]
        ).map((facilitator, index) => (
          <section
            key={facilitator.name + index}
            className="flex items-center gap-3 rounded-xl bg-accent-soft p-3"
          >
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-surface text-sm font-bold text-accent shadow-sm">
              {initials(facilitator.name, "F")}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-app-ink">{facilitator.name}</p>
              <p className="mt-0.5 flex items-center gap-1 text-[10px] text-app-muted">
                <UserRound size={11} className="text-accent" />
                {t("facilitatorOrdinal", { number: index + 1 })}
              </p>
            </div>
          </section>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <MetricCard
          label={t("moduleCompletedMetric")}
          value={completedTeachers}
          total={total}
          percent={completionPercent}
          status={t("percentCompleted", { percent: completionPercent })}
          tone="success"
        />
        <MetricCard
          label={t("loginStatus")}
          value={stats.online}
          total={total}
          percent={onlinePercent}
          tone="accent"
        />
      </div>

      <section className="rounded-xl bg-surface p-3 shadow-raised">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-accent-soft text-accent">
              <UsersRound size={16} />
            </span>
            <div className="min-w-0">
              <h2 className="truncate text-sm font-semibold text-app-ink">{t("teacherRoster")}</h2>
              <p className="truncate text-[9px] text-app-muted">{t("individualCompletionLogin")}</p>
            </div>
          </div>
          <span className="shrink-0 rounded-full bg-accent-soft px-2 py-1 text-[9px] font-semibold text-accent">
            {t("enrolledCount", { count: total })}
          </span>
        </div>

        {roster.length === 0 ? (
          <div className="grid justify-items-center gap-2 rounded-lg bg-app-field px-3 py-6 text-center">
            <GraduationCap size={20} className="text-app-muted" />
            <p className="text-xs text-app-muted">{t("emptyRosterProgress")}</p>
          </div>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {roster.map((teacher) => (
              <TeacherRow
                key={teacher.teacher_id}
                teacher={teacher}
                completed={
                  Boolean(teacher.module_submitted) ||
                  (itemCount > 0 && teacher.activities_completed >= itemCount)
                }
                completedLabel={t("moduleCompleted")}
                inProgressLabel={t("inProgress")}
                loggedInLabel={t("loggedIn")}
                offlineLabel={t("offline")}
              />
            ))}
          </ul>
        )}
      </section>

      {detail.status === "ended" && (
        <NavLink
          to={`/attendance/${sessionID}`}
          className="text-center text-xs font-semibold text-accent"
        >
          {t("viewAttendanceRegister")}
        </NavLink>
      )}
    </div>
  );
}
